import type { Sensor } from '@si/types';
import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface Params {
  projectId: string;
  installationId: string;
  sensorId: number;
}

export function useDeactivateSensor() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: ({ projectId, sensorId }: Params) =>
      post({ url: `/projects/${projectId}/sensors/${sensorId}/deactivate` }),
    onMutate: async ({ projectId, sensorId }) => {
      const activeSensorQueryFilter = {
        predicate: ({ queryKey }: { queryKey: readonly unknown[] }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'sensors') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string; status?: string } | undefined;
          return queryParams?.projectId === projectId && queryParams?.status === 'active';
        },
      };

      await queryClient.cancelQueries(activeSensorQueryFilter);
      const previousSensorQueries = queryClient.getQueriesData<{ sensors: Sensor[] }>(
        activeSensorQueryFilter
      );

      previousSensorQueries.forEach(([queryKey, queryData]) => {
        if (!queryData) return;

        queryClient.setQueryData(queryKey, {
          sensors: queryData.sensors.filter((sensor) => sensor.sensorId !== sensorId),
        });
      });

      return { previousSensorQueries };
    },
    onError: (_err, _variables, context) => {
      context?.previousSensorQueries?.forEach(([queryKey, queryData]) => {
        queryClient.setQueryData(queryKey, queryData);
      });
    },
    onSettled: (_data, _error, { projectId }) => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'sensors') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string } | undefined;
          return queryParams?.projectId === projectId;
        },
      });
    },
  });

  const deactivateSensor = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    deactivateSensor,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
